import { safeUpdateDoc, safeAddDoc, safeDeleteDoc } from '../utils';
import { useState, useEffect, useMemo, useRef } from 'react';
import { Readability } from '@mozilla/readability';
import {
  Rss,
  Plus,
  ChevronRight,
  ChevronDown,
  ExternalLink,
  Trash2,
  RefreshCw,
  X,
  FolderPlus,
  Folder,
  LayoutList,
  Maximize2,
  List,
  CheckCheck,
  Settings,
  Download,
  Upload,
  Star,
  Shield,
  BookOpen,
} from 'lucide-react';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  serverTimestamp,
  orderBy,
} from 'firebase/firestore';
import { db } from '../firebase';
import { appId } from '../constants';

const RSSView = ({ user }) => {
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const startPos = useRef({ x: 0, y: 0 });

  const handleMouseDown = (e) => {
    if (e.target.closest('button') || e.target.closest('input') || e.target.closest('select')) return;
    setIsDragging(true);
    startPos.current = { x: e.clientX - pos.x, y: e.clientY - startPos.current.y };
  };

  useEffect(() => {
    if (!isDragging) return;
    const handleMouseMove = (e) => {
      setPos({ x: e.clientX - startPos.current.x, y: e.clientY - startPos.current.y });
    };
    const handleMouseUp = () => setIsDragging(false);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const handleBackdropWheel = (e) => {
    const calendar = document.querySelector('.custom-scrollbar');
    if (calendar) {
      calendar.scrollTop += e.deltaY;
    }
  };

  const [feeds, setFeeds] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState('all'); // 'all', 'unread', or folderId
  const [newFeedUrl, setNewChannelUrl] = useState('');
  const [newFolderName, setNewFolderName] = useState('');

  const handleDeleteFolder = async (id) => {
    if (window.confirm('Are you sure you want to delete this folder? Feeds inside will be moved to the main list.')) {
      await safeDeleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'rss_folders', id));
    }
  };

  const handleDeleteFeed = async (id) => {
    if (window.confirm('Are you sure you want to delete this feed?')) {
      await safeDeleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'rss_feeds', id));
    }
  };
  const [isAddingFolder, setIsAddingFolder] = useState(false);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'compact', 'list', 'full'
  const [readingArticle, setReadingArticle] = useState(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [targetFolderId, setTargetFolderId] = useState('');
  const [collapsedFeedIds, setCollapsedFeedIds] = useState({});
  const [collapsedSidebarFolders, setCollapsedSidebarFolders] = useState({});
  const [readerModeLoading, setReaderModeLoading] = useState(false);

  const fetchFullText = async () => {
    if (!readingArticle?.link) return;
    setReaderModeLoading(true);
    try {
      const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(readingArticle.link)}`;
      const response = await fetch(proxyUrl);
      const data = await response.json();
      const html = data.contents;
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      const reader = new Readability(doc);
      const article = reader.parse();
      
      if (article && article.content) {
        setReadingArticle(prev => ({
          ...prev,
          description: article.content,
          isFullText: true,
        }));
      } else {
        alert("Couldn't extract full text from this article.");
      }
    } catch (e) {
      console.error(e);
      alert("Failed to load reader mode.");
    }
    setReaderModeLoading(false);
  };

  const uid = user?.uid || 'test-uid-123';

  useEffect(() => {
    const q = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'rss_feeds'),
      where('addedBy', '==', uid)
    );
    const unsubFeeds = onSnapshot(q, (snap) => {
      const docs = snap.docs.map((d) => ({ ...d.data(), id: d.id }));
      setFeeds(docs);
    });

    const fq = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'rss_folders'),
      where('addedBy', '==', uid),
      orderBy('name')
    );
    const unsubFolders = onSnapshot(fq, (snap) => {
      setFolders(snap.docs.map((d) => ({ ...d.data(), id: d.id })));
    });

    return () => {
      unsubFeeds();
      unsubFolders();
    };
  }, [uid]);

  const fetchFeed = async (url) => {
    try {
      const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(url)}`;
      const response = await fetch(proxyUrl);
      const data = await response.json();
      const text = data.contents;
      if (!text) return null;

      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(text, 'text/xml');

      const title =
        xmlDoc.querySelector('channel > title')?.textContent ||
        xmlDoc.querySelector('feed > title')?.textContent ||
        'Untitled Feed';
      const items = xmlDoc.querySelectorAll('item, entry');

      const articles = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const aTitle = item.querySelector('title')?.textContent;
        const aLink =
          item.querySelector('link')?.getAttribute('href') ||
          item.querySelector('link')?.textContent ||
          item.querySelector('link')?.getAttribute('url');
        const aPubDate =
          item.querySelector('pubDate')?.textContent ||
          item.querySelector('published')?.textContent ||
          item.querySelector('updated')?.textContent;

        const contentEncoded =
          item.getElementsByTagNameNS('*', 'encoded')[0]?.textContent ||
          item.getElementsByTagName('content:encoded')[0]?.textContent;
        const standardContent = item.querySelector('content')?.textContent;
        const description = item.querySelector('description')?.textContent;
        const summary = item.querySelector('summary')?.textContent;

        const aDesc = contentEncoded || standardContent || description || summary;

        if (aTitle && aLink) {
          articles.push({
            title: aTitle,
            link: aLink.trim(),
            pubDate: aPubDate,
            description: aDesc,
          });
        }
      }
      return { title, articles };
    } catch (e) {
      console.error('Failed to fetch RSS feed:', e);
      return null;
    }
  };

  const discoverRssFeed = async (url) => {
    let normalizedUrl = url.trim();
    if (!normalizedUrl.startsWith('http')) normalizedUrl = `https://${normalizedUrl}`;

    try {
      const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(normalizedUrl)}`;
      const response = await fetch(proxyUrl);
      const data = await response.json();
      const html = data.contents;
      if (!html) return normalizedUrl;

      if (html.includes('<rss') || html.includes('<feed')) {
        return normalizedUrl;
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const rssLink =
        doc.querySelector('link[type="application/rss+xml"]') ||
        doc.querySelector('link[type="application/atom+xml"]');

      if (rssLink) {
        let href = rssLink.getAttribute('href');
        if (href && !href.startsWith('http')) {
          const base = new URL(normalizedUrl);
          href = new URL(href, base.origin).href;
        }
        return href;
      }
      return normalizedUrl;
    } catch (e) {
      return normalizedUrl;
    }
  };

  const handleAddFeed = async (e) => {
    e.preventDefault();
    if (!newFeedUrl.trim()) return;

    setLoading(true);
    const actualFeedUrl = await discoverRssFeed(newFeedUrl);
    const feedData = await fetchFeed(actualFeedUrl);

    if (feedData) {
      await safeAddDoc(collection(db, 'artifacts', appId, 'public', 'data', 'rss_feeds'), {
        url: actualFeedUrl,
        originalUrl: newFeedUrl,
        name: feedData.title,
        articles: feedData.articles,
        readArticles: [],
        folderId: targetFolderId || null,
        lastFetched: serverTimestamp(),
        addedBy: uid,
      });
      setNewChannelUrl('');
      setTargetFolderId('');
    } else {
      alert('Failed to fetch RSS feed. Please check the URL.');
    }
    setLoading(false);
  };

  const handleAddFolder = async (e) => {
    e.preventDefault();
    if (!newFolderName.trim()) return;
    await safeAddDoc(collection(db, 'artifacts', appId, 'public', 'data', 'rss_folders'), {
      name: newFolderName,
      addedBy: uid,
      createdAt: serverTimestamp(),
    });
    setNewFolderName('');
    setIsAddingFolder(false);
  };

  const toggleRead = async (feedId, articleLink, currentReadArticles) => {
    const readArticles = [...(currentReadArticles || [])];
    const idx = readArticles.indexOf(articleLink);
    if (idx > -1) {
      readArticles.splice(idx, 1);
    } else {
      readArticles.push(articleLink);
    }
    await safeUpdateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'rss_feeds', feedId), {
      readArticles,
    });
  };

  const toggleStar = async (feedId, articleLink, currentStarredArticles) => {
    const starredArticles = [...(currentStarredArticles || [])];
    const idx = starredArticles.indexOf(articleLink);
    if (idx > -1) {
      starredArticles.splice(idx, 1);
    } else {
      starredArticles.push(articleLink);
    }
    await safeUpdateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'rss_feeds', feedId), {
      starredArticles,
    });
  };

  const toggleKeepFullContent = async (feedId, currentVal) => {
    await safeUpdateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'rss_feeds', feedId), {
      keepFullContent: !currentVal,
    });
  };

  const handleMoveFeed = async (feedId, folderId) => {
    await safeUpdateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'rss_feeds', feedId), {
      folderId: folderId || null,
    });
  };

  const toggleFeedCollapse = (feedId) => {
    setCollapsedFeedIds((prev) => ({ ...prev, [feedId]: !prev[feedId] }));
  };

  const markAllRead = async (feed) => {
    const allLinks = (feed.articles || []).map((a) => a.link);
    await safeUpdateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'rss_feeds', feed.id), {
      readArticles: allLinks,
    });
  };

  const refreshFeed = async (feed) => {
    const feedData = await fetchFeed(feed.url);
    if (feedData) {
      const merged = [...(feed.articles || [])];
      let newCount = 0;
      feedData.articles.forEach((na) => {
        if (!merged.find((ma) => ma.link === na.link)) {
          merged.unshift(na);
          newCount++;
        }
      });
      
      const finalArticles = merged.map((a, i) => {
        if (!feed.keepFullContent && i >= 50 && !(feed.starredArticles || []).includes(a.link)) {
          return { ...a, description: null };
        }
        return a;
      });

      await safeUpdateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'rss_feeds', feed.id), {
        articles: finalArticles,
        lastFetched: serverTimestamp(),
      });
      return newCount;
    }
    return 0;
  };

  const refreshAll = async () => {
    setLoading(true);
    for (const feed of feeds) {
      await refreshFeed(feed);
    }
    setLoading(false);
  };

  const exportOPML = () => {
    let opml = `<?xml version="1.0" encoding="UTF-8"?>\n<opml version="1.0">\n  <head>\n    <title>RSS Subscriptions</title>\n  </head>\n  <body>\n`;

    // Group by folders
    folders.forEach((folder) => {
      opml += `    <outline text="${folder.name}" title="${folder.name}">\n`;
      feeds
        .filter((f) => f.folderId === folder.id)
        .forEach((feed) => {
          opml += `      <outline type="rss" text="${feed.name}" title="${feed.name}" xmlUrl="${feed.url}" htmlUrl="${feed.url}"/>\n`;
        });
      opml += `    </outline>\n`;
    });

    // Uncategorized
    feeds
      .filter((f) => !f.folderId)
      .forEach((feed) => {
        opml += `    <outline type="rss" text="${feed.name}" title="${feed.name}" xmlUrl="${feed.url}" htmlUrl="${feed.url}"/>\n`;
      });

    opml += `  </body>\n</opml>`;

    const blob = new Blob([opml], { type: 'text/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `subscriptions_${new Date().toISOString().split('T')[0]}.opml`;
    a.click();
  };

  const importOPML = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLoading(true);
    try {
      const text = await file.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(text, 'text/xml');
      const outlines = xmlDoc.querySelectorAll('body > outline');

      for (const outline of outlines) {
        const isFolder = outline.getAttribute('xmlUrl') === null;
        if (isFolder) {
          const folderName = outline.getAttribute('title') || outline.getAttribute('text');
          // Create folder
          const folderRef = await safeAddDoc(
            collection(db, 'artifacts', appId, 'public', 'data', 'rss_folders'),
            {
              name: folderName,
              addedBy: uid,
              createdAt: serverTimestamp(),
            }
          );

          const subOutlines = outline.querySelectorAll('outline');
          for (const sub of subOutlines) {
            const url = sub.getAttribute('xmlUrl');
            const name = sub.getAttribute('title') || sub.getAttribute('text');
            if (url) {
              await safeAddDoc(collection(db, 'artifacts', appId, 'public', 'data', 'rss_feeds'), {
                url,
                name,
                articles: [],
                readArticles: [],
                folderId: folderRef.id,
                addedBy: uid,
                lastFetched: serverTimestamp(),
              });
            }
          }
        } else {
          const url = outline.getAttribute('xmlUrl');
          const name = outline.getAttribute('title') || outline.getAttribute('text');
          if (url) {
            await safeAddDoc(collection(db, 'artifacts', appId, 'public', 'data', 'rss_feeds'), {
              url,
              name,
              articles: [],
              readArticles: [],
              folderId: null,
              addedBy: uid,
              lastFetched: serverTimestamp(),
            });
          }
        }
      }
      alert('OPML Imported successfully!');
    } catch (e) {
      console.error('Failed to import OPML:', e);
      alert('Error importing OPML file.');
    }
    setLoading(false);
    setIsSettingsOpen(false);
  };

  const purgeAll = async () => {
    if (
      window.confirm(
        'ARE YOU ABSOLUTELY SURE? This will delete all your feeds and folders permanently.'
      )
    ) {
      setLoading(true);
      for (const feed of feeds) {
        await safeDeleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'rss_feeds', feed.id));
      }
      for (const folder of folders) {
        await safeDeleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'rss_folders', folder.id));
      }
      setLoading(false);
      setIsSettingsOpen(false);
    }
  };

  const handleToggleMuteKeyword = async (folder, keyword) => {
    const mutedKeywords = [...(folder.mutedKeywords || [])];
    const idx = mutedKeywords.indexOf(keyword);
    if (idx > -1) {
      mutedKeywords.splice(idx, 1);
    } else {
      mutedKeywords.push(keyword.toLowerCase());
    }
    await safeUpdateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'rss_folders', folder.id), {
      mutedKeywords,
    });
  };

  const isArticleMuted = (article, folder) => {
    if (!folder || !folder.mutedKeywords?.length) return false;
    const text = (article.title + ' ' + article.description).toLowerCase();
    return folder.mutedKeywords.some((k) => text.includes(k));
  };

  const filteredFeeds = useMemo(() => {
    let result = feeds;
    if (selectedFolderId === 'unread') {
      result = feeds.filter((f) => (f.articles?.length || 0) > (f.readArticles?.length || 0));
    } else if (selectedFolderId === 'starred') {
      result = feeds.filter((f) => (f.starredArticles?.length || 0) > 0);
    } else if (selectedFolderId === 'uncategorized') {
      result = feeds.filter((f) => !f.folderId);
    } else if (selectedFolderId !== 'all') {
      result = feeds.filter((f) => f.folderId === selectedFolderId);
    }
    return result;
  }, [feeds, selectedFolderId]);

  const getUnreadCount = (feed) => {
    const total = feed.articles?.length || 0;
    const read = feed.readArticles?.length || 0;
    return Math.max(0, total - read);
  };

  const getTotalUnread = () => {
    return feeds.reduce((sum, f) => sum + getUnreadCount(f), 0);
  };

  const getTotalStarred = () => {
    return feeds.reduce((sum, f) => sum + (f.starredArticles?.length || 0), 0);
  };

  useEffect(() => {
    const checkAndRefresh = () => {
      const now = Date.now();
      const TWO_HOURS = 2 * 60 * 60 * 1000;
      feeds.forEach(f => {
        if (!f.lastFetched) return;
        const fetchedAt = f.lastFetched?.toMillis ? f.lastFetched.toMillis() : f.lastFetched;
        if ((now - fetchedAt) > TWO_HOURS) {
          refreshFeed(f);
        }
      });
    };
    checkAndRefresh();
    const interval = setInterval(checkAndRefresh, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, [feeds]);


  if (readingArticle) {
    return (
      <div className="flex-1 h-full bg-white flex flex-col overflow-hidden animate-in fade-in duration-300">
        <header className="h-14 border-b border-zinc-100 flex items-center justify-between px-6 bg-white/80 backdrop-blur-md z-10">
          <div className="flex items-center gap-4 overflow-hidden">
            <button
              onClick={() => setReadingArticle(null)}
              className="p-2 -ml-2 hover:bg-zinc-100 rounded-full transition-colors text-zinc-400 hover:text-zinc-900"
            >
              <ChevronRight size={20} className="rotate-180" />
            </button>
            <h2 className="text-xs font-black uppercase tracking-widest text-zinc-900 truncate">
              {readingArticle.title}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {!readingArticle.isFullText && (
              <button
                onClick={fetchFullText}
                disabled={readerModeLoading}
                className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-blue-100 transition-colors disabled:opacity-50"
              >
                {readerModeLoading ? <RefreshCw size={14} className="animate-spin" /> : <BookOpen size={14} />}
                Reader Mode
              </button>
            )}
            <a
              href={readingArticle.link}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 hover:bg-zinc-100 rounded-full text-zinc-400 hover:text-zinc-900"
            >
              <ExternalLink size={20} />
            </a>
            <button
              onClick={() => setReadingArticle(null)}
              className="p-2 hover:bg-zinc-100 rounded-full text-zinc-400 hover:text-zinc-900"
            >
              <X size={20} />
            </button>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto p-10 custom-scrollbar bg-zinc-50/30">
          <article className="max-w-3xl mx-auto bg-white p-12 rounded-3xl shadow-sm border border-zinc-100">
            <h1 className="text-4xl font-black tracking-tighter text-zinc-900 mb-6 leading-tight">
              {readingArticle.title}
            </h1>
            <div className="flex items-center gap-4 text-xs font-bold text-zinc-400 uppercase tracking-widest mb-10 border-b border-zinc-100 pb-6">
              <span>
                {readingArticle.pubDate
                  ? new Date(readingArticle.pubDate).toLocaleDateString()
                  : 'Unknown Date'}
              </span>
              <div className="w-1 h-1 bg-zinc-200 rounded-full" />
              <span className="text-zinc-900">{readingArticle.feedName}</span>
            </div>
            <div
              className="text-zinc-700 leading-relaxed space-y-6 text-lg"
              dangerouslySetInnerHTML={{ __html: readingArticle.description }}
            />
          </article>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 h-full bg-white flex overflow-hidden">
      {/* Folder Sidebar */}
      <aside className="w-64 border-r border-zinc-100 flex flex-col bg-zinc-50/50">
        <div className="p-6 border-b border-zinc-100 bg-white">
          <div className="flex items-center justify-between mb-6">
            <h1 data-testid="rss-heading" className="text-xl font-black tracking-tighter uppercase">
              Reader
            </h1>
            <Rss size={20} className="text-blue-600" />
          </div>
          <button
            onClick={() => setIsAddingFolder(true)}
            className="w-full py-2 bg-zinc-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-black transition-all"
          >
            <FolderPlus size={14} />
            New Folder
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-4 space-y-1 custom-scrollbar">
          <button
            onClick={() => setSelectedFolderId('all')}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-xl transition-all ${selectedFolderId === 'all' ? 'bg-white shadow-sm border border-zinc-200 text-zinc-900' : 'text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100'}`}
          >
            <div className="flex items-center gap-3">
              <List size={16} />
              <span className="text-xs font-bold">All Feeds</span>
            </div>
            <span className="text-[10px] font-black">{feeds.length}</span>
          </button>
          <button
            onClick={() => setSelectedFolderId('unread')}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-xl transition-all ${selectedFolderId === 'unread' ? 'bg-white shadow-sm border border-zinc-200 text-blue-600' : 'text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100'}`}
          >
            <div className="flex items-center gap-3">
              <RefreshCw size={16} />
              <span className="text-xs font-bold">Unread</span>
            </div>
            <span className="text-[10px] font-black bg-blue-100 px-1.5 py-0.5 rounded-full">
              {getTotalUnread()}
            </span>
          </button>
          <button
            onClick={() => setSelectedFolderId('starred')}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-xl transition-all ${selectedFolderId === 'starred' ? 'bg-white shadow-sm border border-zinc-200 text-amber-500' : 'text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100'}`}
          >
            <div className="flex items-center gap-3">
              <Star size={16} className={selectedFolderId === 'starred' ? "fill-amber-500" : ""} />
              <span className="text-xs font-bold">Starred</span>
            </div>
            <span className="text-[10px] font-black bg-amber-100 px-1.5 py-0.5 rounded-full text-amber-700">
              {getTotalStarred()}
            </span>
          </button>

          <div className="pt-4 pb-2">
            <span className="px-3 text-[10px] font-black text-zinc-400 uppercase tracking-widest">
              Folders
            </span>
          </div>

          {folders.map((folder) => (
            <div key={folder.id} className="space-y-1">
              <div className="group relative">
                <button
                  onClick={() => setSelectedFolderId(folder.id)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-xl transition-all ${selectedFolderId === folder.id ? 'bg-white shadow-sm border border-zinc-200 text-zinc-900' : 'text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100'}`}
                >
                  <div className="flex items-center gap-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCollapsedSidebarFolders((prev) => ({
                          ...prev,
                          [folder.id]: !prev[folder.id],
                        }));
                      }}
                      className="p-0.5 hover:bg-zinc-200 rounded transition-colors"
                    >
                      {collapsedSidebarFolders[folder.id] ? (
                        <ChevronRight size={12} />
                      ) : (
                        <ChevronDown size={12} />
                      )}
                    </button>
                    <Folder
                      size={16}
                      className={selectedFolderId === folder.id ? 'text-blue-500' : ''}
                    />
                    <span className="text-xs font-bold truncate max-w-[100px]">{folder.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-black">
                      {feeds.filter((f) => f.folderId === folder.id).length}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteFolder(folder.id);
                      }}
                      className="opacity-0 group-hover:opacity-100 p-1 hover:text-red-600 transition-all"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </button>
              </div>

              {!collapsedSidebarFolders[folder.id] && (
                <div className="ml-6 space-y-0.5 border-l border-zinc-100 pl-2">
                  {feeds
                    .filter((f) => f.folderId === folder.id)
                    .map((feed) => (
                      <button
                        key={feed.id}
                        onClick={() => setSelectedFolderId(`feed:${feed.id}`)}
                        className={`w-full flex items-center gap-2 px-2 py-1 rounded-lg text-[10px] font-bold transition-all ${selectedFolderId === `feed:${feed.id}` ? 'text-blue-600 bg-blue-50' : 'text-zinc-400 hover:text-zinc-900 hover:bg-zinc-50'}`}
                      >
                        <img
                          src={`https://www.google.com/s2/favicons?domain=${new URL(feed.url).hostname}&sz=16`}
                          className="w-3 h-3 rounded-sm grayscale"
                          alt=""
                          onError={(e) => (e.target.src = 'https://lucide.dev/rss.svg')}
                        />
                        <span className="truncate">{feed.name}</span>
                      </button>
                    ))}
                </div>
              )}
            </div>
          ))}

          <button
            onClick={() => setSelectedFolderId('uncategorized')}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-xl transition-all ${selectedFolderId === 'uncategorized' ? 'bg-white shadow-sm border border-zinc-200 text-zinc-900' : 'text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100'}`}
          >
            <div className="flex items-center gap-3">
              <Folder size={16} />
              <span className="text-xs font-bold">Uncategorized</span>
            </div>
            <span className="text-[10px] font-black">
              {feeds.filter((f) => !f.folderId).length}
            </span>
          </button>
        </nav>

        <div className="p-4 border-t border-zinc-100 bg-white">
          <button
            onClick={() => setIsSettingsOpen(true)}
            className="w-full flex items-center gap-3 px-3 py-2 text-zinc-500 hover:text-zinc-900 transition-colors"
          >
            <Settings size={16} />
            <span className="text-xs font-bold">Settings</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col bg-white overflow-hidden">
        <header className="h-16 border-b border-zinc-100 px-8 flex items-center justify-between bg-white/80 backdrop-blur-md z-10">
          <div className="flex items-center gap-4">
            <h2 className="text-sm font-black uppercase tracking-widest text-zinc-900">
              {selectedFolderId === 'all'
                ? 'All Feeds'
                : selectedFolderId === 'unread'
                  ? 'Unread Articles'
                  : selectedFolderId === 'uncategorized'
                    ? 'Uncategorized'
                    : folders.find((f) => f.id === selectedFolderId)?.name || 'Folder'}
            </h2>
            <div className="flex items-center bg-zinc-100 p-1 rounded-xl">
              <button
                onClick={() => setViewMode('compact')}
                className={`p-1.5 rounded-lg transition-all ${viewMode === 'compact' ? 'bg-white shadow-sm text-zinc-900' : 'text-zinc-400 hover:text-zinc-600'}`}
              >
                <LayoutList size={14} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded-lg transition-all ${viewMode === 'list' ? 'bg-white shadow-sm text-zinc-900' : 'text-zinc-400 hover:text-zinc-600'}`}
              >
                <List size={14} />
              </button>
              <button
                onClick={() => setViewMode('full')}
                className={`p-1.5 rounded-lg transition-all ${viewMode === 'full' ? 'bg-white shadow-sm text-zinc-900' : 'text-zinc-400 hover:text-zinc-600'}`}
              >
                <Maximize2 size={14} />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={refreshAll}
              disabled={loading}
              className="p-2 text-zinc-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all"
              title="Refresh All Feeds"
            >
              <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
            </button>
            <form onSubmit={handleAddFeed} className="flex gap-2">
              <div className="flex items-center bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-1.5 focus-within:border-blue-500 transition-all">
                <input
                  type="text"
                  placeholder="Add Feed URL..."
                  className="bg-transparent text-xs font-bold outline-none w-48"
                  value={newFeedUrl}
                  onChange={(e) => setNewChannelUrl(e.target.value)}
                />
                <select
                  className="bg-transparent text-[10px] font-black uppercase tracking-widest border-l border-zinc-200 ml-2 pl-2 outline-none cursor-pointer"
                  value={targetFolderId}
                  onChange={(e) => setTargetFolderId(e.target.value)}
                >
                  <option value="">No Folder</option>
                  {folders.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-xl font-black uppercase tracking-widest text-[10px] hover:bg-blue-700 transition-all flex items-center gap-2 shadow-lg shadow-blue-100"
              >
                {loading ? <RefreshCw size={12} className="animate-spin" /> : <Plus size={14} />}
                Add
              </button>
            </form>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-8 custom-scrollbar space-y-8">
          {filteredFeeds.map((feed) => (
            <section key={feed.id} className="space-y-4">
              <div className="flex items-center justify-between border-b border-zinc-100 pb-2">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => toggleFeedCollapse(feed.id)}
                    className="p-1 hover:bg-zinc-100 rounded transition-colors text-zinc-400 hover:text-zinc-900"
                  >
                    {collapsedFeedIds[feed.id] ? (
                      <ChevronRight size={14} />
                    ) : (
                      <ChevronDown size={14} />
                    )}
                  </button>
                  <img
                    src={`https://www.google.com/s2/favicons?domain=${new URL(feed.url).hostname}&sz=32`}
                    className="w-5 h-5 rounded"
                    alt=""
                    onError={(e) => (e.target.src = 'https://lucide.dev/rss.svg')}
                  />
                  <h3 className="text-xs font-black uppercase tracking-widest text-zinc-900">
                    {feed.name}
                  </h3>
                  <span className="text-[10px] font-black text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                    {getUnreadCount(feed)} Unread
                  </span>
                  <button
                    onClick={() => refreshFeed(feed)}
                    disabled={loading}
                    className="p-1 text-zinc-300 hover:text-zinc-600 transition-colors"
                  >
                    <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center bg-zinc-50 border border-zinc-200 rounded-lg px-2 py-0.5">
                    <Folder size={10} className="text-zinc-400 mr-1.5" />
                    <select
                      className="bg-transparent text-[9px] font-black uppercase tracking-widest outline-none cursor-pointer text-zinc-500 hover:text-zinc-900"
                      value={feed.folderId || ''}
                      onChange={(e) => handleMoveFeed(feed.id, e.target.value)}
                    >
                      <option value="">No Folder</option>
                      {folders.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    onClick={() => toggleKeepFullContent(feed.id, feed.keepFullContent)}
                    className={`p-1.5 transition-colors ${feed.keepFullContent ? 'text-amber-500' : 'text-zinc-300 hover:text-zinc-600'}`}
                    title="Keep full articles forever"
                  >
                    <Shield size={16} className={feed.keepFullContent ? 'fill-amber-500/20' : ''} />
                  </button>
                  <button
                    onClick={() => markAllRead(feed)}
                    className="p-1.5 text-zinc-400 hover:text-blue-600 transition-colors"
                    title="Mark all as read"
                  >
                    <CheckCheck size={16} />
                  </button>
                  <button
                    onClick={() => handleDeleteFeed(feed.id)}
                    className="p-1.5 text-zinc-400 hover:text-red-600 transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {!collapsedFeedIds[feed.id] && (
                <div
                  className={
                    viewMode === 'compact'
                      ? 'space-y-1'
                      : viewMode === 'list'
                        ? 'grid grid-cols-1 gap-3'
                        : 'space-y-6'
                  }
                >
                  {feed.articles?.map((article, idx) => {
                    const isRead = (feed.readArticles || []).includes(article.link);
                    const isStarred = (feed.starredArticles || []).includes(article.link);
                    if (selectedFolderId === 'unread' && isRead) return null;
                    if (selectedFolderId === 'starred' && !isStarred) return null;

                    const currentFolder = folders.find((f) => f.id === feed.folderId);
                    if (isArticleMuted(article, currentFolder)) return null;

                    if (viewMode === 'compact') {
                      return (
                        <div
                          key={idx}
                          onClick={() => {
                            setReadingArticle({ ...article, feedName: feed.name });
                            toggleRead(feed.id, article.link, feed.readArticles);
                          }}
                          className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-all ${isRead ? 'opacity-50 grayscale' : 'hover:bg-zinc-50'}`}
                        >
                          <div className="flex items-center gap-4 flex-1 min-w-0">
                            <span className="text-[10px] font-bold text-zinc-400 w-20 flex-shrink-0">
                              {article.pubDate
                                ? new Date(article.pubDate).toLocaleDateString([], {
                                    month: 'short',
                                    day: 'numeric',
                                  })
                                : ''}
                            </span>
                            <span
                              className={`text-xs truncate flex-1 ${isRead ? 'font-medium text-zinc-500' : 'font-bold text-zinc-900'}`}
                            >
                              {article.title}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleStar(feed.id, article.link, feed.starredArticles);
                              }}
                              className={`p-1 rounded transition-colors ${isStarred ? 'text-amber-500 hover:bg-amber-50' : 'text-zinc-300 hover:text-amber-500'}`}
                            >
                              <Star size={14} className={isStarred ? "fill-amber-500" : ""} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleRead(feed.id, article.link, feed.readArticles);
                              }}
                              className={`p-1 rounded transition-colors ${isRead ? 'text-blue-600 hover:bg-blue-50' : 'text-zinc-300 hover:text-zinc-900'}`}
                            >
                              <CheckCheck size={14} />
                            </button>
                          </div>
                        </div>
                      );
                    }

                    if (viewMode === 'list') {
                      return (
                        <div
                          key={idx}
                          onClick={() => {
                            setReadingArticle({ ...article, feedName: feed.name });
                            toggleRead(feed.id, article.link, feed.readArticles);
                          }}
                          className={`group p-4 rounded-2xl border transition-all cursor-pointer ${isRead ? 'bg-zinc-50 border-zinc-100 grayscale-[0.5] opacity-60' : 'bg-white border-zinc-100 hover:border-blue-200 hover:shadow-md'}`}
                        >
                          <div className="flex justify-between gap-6">
                            <div className="flex-1 space-y-2">
                              <div className="flex items-center gap-3 text-[10px] font-black uppercase tracking-widest text-zinc-400">
                                <span>
                                  {article.pubDate
                                    ? new Date(article.pubDate).toLocaleDateString()
                                    : ''}
                                </span>
                                {!isRead && (
                                  <div className="w-1.5 h-1.5 bg-blue-600 rounded-full" />
                                )}
                              </div>
                              <h4
                                className={`text-sm leading-snug ${isRead ? 'font-bold text-zinc-600' : 'font-black text-zinc-900'}`}
                              >
                                {article.title}
                              </h4>
                              <p
                                className="text-xs text-zinc-500 line-clamp-2 font-medium"
                                dangerouslySetInnerHTML={{
                                  __html: article.description
                                    ?.replace(/<[^>]*>/g, '')
                                    .slice(0, 200),
                                }}
                              />
                            </div>
                            <div className="flex flex-col gap-2 items-center justify-start opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleStar(feed.id, article.link, feed.starredArticles);
                                }}
                                className={`p-1.5 rounded transition-colors ${isStarred ? 'text-amber-500 bg-amber-50' : 'text-zinc-400 hover:text-amber-500 hover:bg-zinc-100'}`}
                              >
                                <Star size={16} className={isStarred ? "fill-amber-500" : ""} />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div
                        key={idx}
                        className={`p-8 rounded-3xl border border-zinc-100 space-y-6 ${isRead ? 'opacity-50 grayscale-[0.5]' : ''}`}
                      >
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-black uppercase tracking-widest text-blue-600">
                              {article.pubDate
                                ? new Date(article.pubDate).toLocaleDateString()
                                : ''}
                            </span>
                            <div className="flex gap-2">
                              <button
                                onClick={() => toggleStar(feed.id, article.link, feed.starredArticles)}
                                className={`p-2 rounded-full transition-colors ${isStarred ? 'text-amber-500 bg-amber-50' : 'text-zinc-400 hover:text-amber-500 hover:bg-zinc-50'}`}
                              >
                                <Star size={18} className={isStarred ? "fill-amber-500" : ""} />
                              </button>
                              <button
                                onClick={() => toggleRead(feed.id, article.link, feed.readArticles)}
                                className="p-2 hover:bg-zinc-100 rounded-full text-zinc-400 hover:text-blue-600"
                              >
                                <CheckCheck size={18} />
                              </button>
                              <a
                                href={article.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 hover:bg-zinc-100 rounded-full text-zinc-400 hover:text-zinc-900"
                              >
                                <ExternalLink size={18} />
                              </a>
                            </div>
                          </div>
                          <h4 className="text-xl font-black text-zinc-900 leading-tight">
                            {article.title}
                          </h4>
                        </div>
                        <div
                          className="text-sm text-zinc-600 leading-relaxed max-w-none prose prose-zinc"
                          dangerouslySetInnerHTML={{ __html: article.description }}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          ))}

          {filteredFeeds.length === 0 && (
            <div className="text-center py-40">
              <div className="inline-flex p-6 bg-zinc-50 rounded-full mb-6">
                <Rss size={48} className="text-zinc-200" />
              </div>
              <h3 className="text-lg font-black text-zinc-900 uppercase tracking-tighter">
                No Articles to show
              </h3>
              <p className="text-sm text-zinc-400 font-medium">
                Try changing your filters or adding a new feed.
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Modals */}
      {isAddingFolder && (
        <div 
          className="fixed inset-0 bg-black/10 z-50 flex items-center justify-center p-6"
          onClick={() => setIsAddingFolder(false)}
          onWheel={handleBackdropWheel}
        >
          <div 
            data-testid="rss-folder-modal"
            className="bg-white w-full max-w-md rounded-3xl shadow-2xl p-8 animate-in zoom-in-95 duration-200 pointer-events-auto cursor-grab active:cursor-grabbing"
            style={{ transform: `translate(${pos.x}px, ${pos.y}px)` }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={handleMouseDown}
          >
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-black tracking-tighter uppercase">New Folder</h2>
              <button
                onClick={() => setIsAddingFolder(false)}
                className="p-2 hover:bg-zinc-100 rounded-full text-zinc-400"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleAddFolder} className="space-y-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-zinc-400">
                  Folder Name
                </label>
                <input
                  autoFocus
                  type="text"
                  className="w-full bg-zinc-50 border border-zinc-200 rounded-2xl px-4 py-3 text-sm font-bold outline-none focus:border-blue-500 transition-all"
                  placeholder="e.g. Technology, Morning Brew"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                />
              </div>
              <button
                type="submit"
                className="w-full py-4 bg-blue-600 text-white rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-blue-700 shadow-xl shadow-blue-100 transition-all"
              >
                Create Folder
              </button>
            </form>
          </div>
        </div>
      )}

      {isSettingsOpen && (
        <div 
          className="fixed inset-0 bg-black/10 z-50 flex items-center justify-center p-6"
          onClick={() => setIsSettingsOpen(false)}
          onWheel={handleBackdropWheel}
        >
          <div 
            data-testid="rss-settings-modal"
            className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 pointer-events-auto cursor-grab active:cursor-grabbing"
            style={{ transform: `translate(${pos.x}px, ${pos.y}px)` }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={handleMouseDown}
          >
            <header className="px-8 py-6 border-b border-zinc-100 flex items-center justify-between">
              <h2 className="text-2xl font-black tracking-tighter uppercase">Reader Settings</h2>
              <button
                onClick={() => setIsSettingsOpen(false)}
                className="p-2 hover:bg-zinc-100 rounded-full text-zinc-400"
              >
                <X size={20} />
              </button>
            </header>
            <div className="p-8 space-y-8">
              <input
                type="file"
                id="opml-import"
                className="hidden"
                accept=".opml,.xml"
                onChange={importOPML}
              />
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={exportOPML}
                  className="flex items-center justify-center gap-3 p-6 bg-zinc-50 rounded-2xl border border-zinc-100 hover:bg-white hover:border-blue-200 transition-all group"
                >
                  <Download size={24} className="text-zinc-400 group-hover:text-blue-600" />
                  <div className="text-left">
                    <span className="block text-xs font-black uppercase tracking-widest text-zinc-900">
                      Export OPML
                    </span>
                    <span className="text-[10px] font-bold text-zinc-400">
                      Take your feeds anywhere
                    </span>
                  </div>
                </button>
                <button
                  onClick={() => document.getElementById('opml-import').click()}
                  className="flex items-center justify-center gap-3 p-6 bg-zinc-50 rounded-2xl border border-zinc-100 hover:bg-white hover:border-blue-200 transition-all group"
                >
                  <Upload size={24} className="text-zinc-400 group-hover:text-blue-600" />
                  <div className="text-left">
                    <span className="block text-xs font-black uppercase tracking-widest text-zinc-900">
                      Import OPML
                    </span>
                    <span className="text-[10px] font-bold text-zinc-400">
                      Bring your Feedly data
                    </span>
                  </div>
                </button>
              </div>

              <div className="space-y-4">
                <h4 className="text-[10px] font-black uppercase tracking-widest text-zinc-400">
                  Folder Filters (Muted Keywords)
                </h4>
                <div className="space-y-3">
                  {folders.map((folder) => (
                    <div
                      key={folder.id}
                      className="p-4 bg-zinc-50 rounded-2xl border border-zinc-100"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-black uppercase text-zinc-900">
                          {folder.name}
                        </span>
                        <div className="flex gap-1">
                          <input
                            type="text"
                            placeholder="Add keyword..."
                            className="bg-white border border-zinc-200 rounded-lg px-2 py-1 text-[10px] font-bold outline-none focus:border-blue-500"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && e.target.value.trim()) {
                                handleToggleMuteKeyword(folder, e.target.value.trim());
                                e.target.value = '';
                              }
                            }}
                          />
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {(folder.mutedKeywords || []).map((k) => (
                          <button
                            key={k}
                            onClick={() => handleToggleMuteKeyword(folder, k)}
                            className="flex items-center gap-1.5 px-2 py-1 bg-white border border-zinc-200 rounded-lg text-[10px] font-bold text-zinc-600 hover:text-red-600 hover:border-red-100 transition-all"
                          >
                            {k} <X size={10} />
                          </button>
                        ))}
                        {(!folder.mutedKeywords || folder.mutedKeywords.length === 0) && (
                          <span className="text-[10px] text-zinc-400 font-medium">
                            No muted keywords
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t border-zinc-100">
                <h4 className="text-[10px] font-black uppercase tracking-widest text-zinc-400">
                  Data Management
                </h4>
                <div className="p-4 bg-red-50 rounded-2xl border border-red-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="block text-xs font-black text-red-900 uppercase">
                        Purge All Feeds
                      </span>
                      <span className="text-[10px] font-bold text-red-600">
                        This will delete all {feeds.length} feeds and folders
                      </span>
                    </div>
                    <button
                      onClick={purgeAll}
                      className="px-4 py-2 bg-red-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-red-700 transition-all"
                    >
                      Delete Everything
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RSSView;
